import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, User, Clock, Phone, Mail, MessageSquare, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { fetchPractitioners, createAppointment } from '../api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const WHATSAPP_NUMBER = '918279765078';

const BookAppointment = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [practitioners, setPractitioners] = useState([]);
  const [formData, setFormData] = useState({
    practitionerId: '',
    name: '',
    email: '',
    phone: '',
    timeSlot: '',
    concerns: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchPractitioners()
      .then((data) => setPractitioners(data))
      .catch(() => setPractitioners([]));
  }, []);

  const selectedPractitioner = practitioners.find(
    p => p.id === formData.practitionerId
  );

  const sendToWhatsApp = () => {
    const message = encodeURIComponent(
      `🌿 *New Appointment Request - Aatreya Herbals*\n\n` +
      `👨‍⚕️ *Doctor:* ${selectedPractitioner?.name}\n` +
      `🩺 *Specialization:* ${selectedPractitioner?.specialization}\n` +
      `📅 *Date:* ${selectedDate.toLocaleDateString()}\n` +
      `⏰ *Time:* ${formData.timeSlot}\n` +
      `💰 *Fee:* ₹${selectedPractitioner?.consultationFee}\n\n` +
      `👤 *Name:* ${formData.name}\n` +
      `📧 *Email:* ${formData.email}\n` +
      `📱 *Phone:* ${formData.phone}\n` +
      (formData.concerns ? `\n📝 *Health Concerns:* ${formData.concerns}` : '')
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createAppointment({
        practitionerId: formData.practitionerId,
        practitionerName: selectedPractitioner?.name,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: selectedDate.toLocaleDateString(),
        timeSlot: formData.timeSlot,
        concerns: formData.concerns,
      });
    } catch (err) {
      // still proceed to WhatsApp even if save fails
    }
    sendToWhatsApp();
    toast({
      title: "Appointment Request Sent! 🎉",
      description: `Your booking has been saved and sent via WhatsApp. Our team will confirm shortly.`,
    });

    setFormData({
      practitionerId: '',
      name: '',
      email: '',
      phone: '',
      timeSlot: '',
      concerns: ''
    });
    setStep(1);
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="text-5xl sm:text-6xl font-bold font-serif">
              Book Your Consultation
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Schedule a personalized consultation with our expert Ayurvedic practitioners
            </p>
            <div className="pt-4">
              <Button
                onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello Aatreya Herbals! I want to book an appointment with a doctor.')}`, '_blank')}
                className="bg-green-500 hover:bg-green-600 text-white"
                size="lg"
                data-testid="quick-whatsapp-booking"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Quick Book on WhatsApp
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3].map((num) => (
              <React.Fragment key={num}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: num * 0.1 }}
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                    step >= num
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {num}
                </motion.div>
                {num < 3 && (
                  <div
                    className={`w-24 h-1 mx-2 transition-all ${
                      step > num ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-card rounded-2xl border border-border shadow-xl p-8"
          >
            <form onSubmit={handleSubmit}>
              {/* Step 1: Select Practitioner */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl font-serif font-bold mb-2">Select Practitioner</h2>
                    <p className="text-muted-foreground">Choose an expert who matches your needs</p>
                  </div>

                  <div className="space-y-4">
                    {practitioners.map((practitioner) => (
                      <motion.div
                        key={practitioner.id}
                        whileHover={{ scale: 1.02 }}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                          formData.practitionerId === practitioner.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() =>
                          setFormData({ ...formData, practitionerId: practitioner.id })
                        }
                      >
                        <div className="flex items-center space-x-4">
                          <img
                            src={practitioner.image}
                            alt={practitioner.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-serif font-semibold text-lg">
                              {practitioner.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {practitioner.specialization}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {practitioner.experience} years exp • ₹{practitioner.consultationFee}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    size="lg"
                    className="w-full"
                    disabled={!formData.practitionerId}
                    onClick={() => setStep(2)}
                  >
                    Continue
                  </Button>
                </div>
              )}

              {/* Step 2: Select Date & Time */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl font-serif font-bold mb-2">Select Date & Time</h2>
                    <p className="text-muted-foreground">
                      Choose a convenient date and time slot
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="mb-3 block">Select Date</Label>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        disabled={(date) => date < new Date()}
                        className="rounded-md border"
                      />
                    </div>

                    <div>
                      <Label className="mb-3 block">Available Time Slots</Label>
                      <div className="space-y-2">
                        {selectedPractitioner?.timeSlots.map((slot) => (
                          <motion.button
                            key={slot}
                            type="button"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-center space-x-2 ${
                              formData.timeSlot === slot
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                            onClick={() => setFormData({ ...formData, timeSlot: slot })}
                          >
                            <Clock className="w-4 h-4" />
                            <span>{slot}</span>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="flex-1"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      className="flex-1"
                      disabled={!formData.timeSlot}
                      onClick={() => setStep(3)}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Your Details */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl font-serif font-bold mb-2">Your Details</h2>
                    <p className="text-muted-foreground">
                      Provide your information to complete booking
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <div className="relative mt-2">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          id="name"
                          type="text"
                          placeholder="Enter your full name"
                          required
                          className="pl-10"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          required
                          className="pl-10"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <div className="relative mt-2">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+91 XXXXX XXXXX"
                          required
                          className="pl-10"
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="concerns">Health Concerns (Optional)</Label>
                      <div className="relative mt-2">
                        <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                        <Textarea
                          id="concerns"
                          placeholder="Briefly describe your health concerns..."
                          className="pl-10 min-h-[100px]"
                          value={formData.concerns}
                          onChange={(e) =>
                            setFormData({ ...formData, concerns: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Booking Summary */}
                  <div className="bg-primary/5 rounded-xl p-6 space-y-3">
                    <h3 className="font-serif font-semibold text-lg">Booking Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Practitioner:</span>
                        <span className="font-medium">{selectedPractitioner?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="font-medium">{selectedDate.toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Time:</span>
                        <span className="font-medium">{formData.timeSlot}</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-border">
                        <span className="text-muted-foreground">Consultation Fee:</span>
                        <span className="font-bold text-primary text-lg">
                          ₹{selectedPractitioner?.consultationFee}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="flex-1"
                      onClick={() => setStep(2)}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      size="lg"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                      data-testid="confirm-booking-whatsapp"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Confirm via WhatsApp
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookAppointment;